<template>
    <div class="login__form">
        <div class="login__form-logo">
            <IconLogo class-name="icon-fill icon_color_blue" />
        </div>

        <div class="login__form-title">
            Личный кабинет брокера
        </div>

        <YandexCaptcha
            ref="yandexCaptcha"
            site-key="ysc1_RTtbAhLxal7jpczqVeKzUcYqOwZQJPYhe8Cg8YhCa5abbc1a"
            language="ru"
            shield-position="top-left"
            :test="false"
            :invisible="true"
            @callback="captchaCallback"
            @passExecuteMethod="getCaptchaExecuter"
            @onChallengeHidden="onChallengeHidden"
            @onNetworkError="onCaptchaError"
            @onJavaScriptError="onCaptchaError"
            @onTokenExpired="onCaptchaError" />

        <!-- Ввод номера для отправки кода -->
        <template v-if="step === 1">
            <div class="login__form-input indent_top_blue">
                <div class="input__wrapper">
                    <div class="input__container">
                        <input
                            id="phone"
                            ref="phoneInput"
                            :value="phone"
                            name="phone"
                            placeholder=" "
                            class="input"
                            @input="onPhoneInput($event.target.value)"
                            @focus="onPhoneFocus"
                            @blur="onPhoneBlur"
                            @keydown.enter="onCodeEnter">
                        <label for="phone"
                               class="input__label">
                            Введите ваш номер телефона
                        </label>
                    </div>
                </div>
            </div>

            <div v-if="agreementText"
                 class="login__form-agreement">
                <STCheckbox id="login-agreement"
                            v-model="loginAgreement"
                            name="login-agreement">
                    <span v-html="agreementText" />
                </STCheckbox>
            </div>

            <div class="login__form-submit">
                <STBaseButton v-if="isSmsAvailable"
                              text="Получить код по СМС"
                              :disabled="isPhoneInvalid || (agreementText && !loginAgreement) || isCallRequesting"
                              icon-load="IconLoad"
                              modify="button_full_width"
                              :icon-load-id="getRandomId"
                              :is-loading="isSMSSending"
                              @click="checkCaptcha" />

                <STBaseButton v-if="isCallAvailable"
                              text="Получить код по звонку"
                              :disabled="isPhoneInvalid || (agreementText && !loginAgreement) || isSMSSending"
                              icon-load="IconLoad"
                              :modify="['button_full_width', {'button_theme_white': isSmsAvailable}]"
                              :icon-load-id="getRandomId"
                              :is-loading="isCallRequesting"
                              @click="checkCaptcha('call')" />
            </div>
        </template>

        <!-- Ввод кода из смс -->
        <template v-else-if="step === 2">
            <p class="login__form-helper indent_top_orange">
                <template v-if="codeType === 'sms'">
                    Мы отправили смс с 6 цифрами - введите их ниже
                </template>
                <template v-else>
                    Робот продиктует 4 цифры - введите их ниже
                </template>
            </p>
            <div class="login__form-input indent_top_yellow">
                <div class="input__wrapper">
                    <div class="input__container">
                        <input
                            v-if="codeType === 'sms'"
                            id="code"
                            ref="codeInput"
                            v-model.trim="code"
                            name="code"
                            type="text"
                            inputmode="numeric"
                            autocomplete="one-time-code"
                            autocorrect="off"
                            autocapitalize="off"
                            spellcheck="false"
                            maxlength="6"
                            pattern="[0-9]*"
                            placeholder=" "
                            :class="['input login__form-code-input', {'is-error': isCodeError}]"
                            @focus="clearCodeError"
                            @input="onCodeInput"
                            @change="syncCodeFromInput"
                            @blur="syncCodeFromInput"
                            @animationstart="onCodeAutofillAnimation"
                            @keydown.enter="onCodeEnter">
                        <input
                            v-if="codeType === 'call'"
                            id="code"
                            v-model.trim="phoneCode"
                            name="code"
                            placeholder=" "
                            :class="['input', {'is-error': isCodeError}]"
                            @focus="clearCodeError"
                            @keydown.enter="onCodeEnter">
                        <label for="code"
                               class="input__label">
                            {{ codeType === 'sms' ? 'Введите код из SMS' : 'Введите озвученные 4 цифры' }}
                        </label>
                        <span class="input__error"
                              v-html="codeErrorText" />
                    </div>
                </div>
            </div>

            <div class="login__form-submit">
                <STBaseButton text="Далее"
                              :disabled="codeType === 'sms' ? isSMSInvalid : isPhoneCodeInvalid"
                              modify="button_full_width"
                              @click="checkSMSCodeInCRM" />
            </div>

            <div class="login__form-submit login__form-timer-button">
                <button
                    v-if="codeType === 'sms'"
                    type="button"
                    :disabled="!isCanSendCode || isSMSSending"
                    class="button button_theme_white button_full_width"
                    @click="checkCaptcha">
                    <span class="button__text">
                        Отправить код повторно
                        <span v-if="!isCanSendCode">
                            через
                            <!-- 180 секунд -->
                            <STCountdown :time="18*1000"
                                         @end="isCanSendCode = true" />
                        </span>
                    </span>
                </button>

                <button
                    v-if="codeType === 'call'"
                    type="button"
                    :disabled="!isCanSendCode || isCallRequesting"
                    class="button button_theme_white button_full_width"
                    @click="checkCaptcha('call')">
                    <span class="button__text">
                        Повторить звонок
                        <span v-if="!isCanSendCode">
                            через
                            <!-- 180 секунд -->
                            <STCountdown :time="18*1000"
                                         @end="isCanSendCode = true" />
                        </span>
                    </span>
                </button>
            </div>


            <p class="login__form-description indent_top_orange">
                <template v-if="codeType === 'sms'">
                    Код отправлен на номер <span class="color-title">{{ phone }}</span>
                </template>
                <template v-else>
                    Звонок заказан на номер <span class="color-title">{{ phone }}</span>
                </template>
            </p>
            <STBaseButton text="Изменить номер"
                          modify="button_theme_reset indent_top_cyan"
                          @click="step = 1" />
        </template>

        <STLoader :loading="loading"
                  :has-loaded="hasLoaded"
                  :message="loaderMessage"
                  modify="loader_full_size" />
    </div>
</template>

<script>
import Utils from '@/helpers/Utils';
import IconLogo from '@/components/Icons/Logo';
import IconLoad from '@/components/Icons/Load';
import {maxLength, minLength, required} from 'vuelidate/lib/validators';
import {mapActions, mapGetters} from 'vuex';
import {AUTH_CHECK_FAILED} from '@/store/settings';
import YandexCaptcha from '@/components/YandexCaptcha/YandexCaptcha';
import {handlePhoneFocus, handlePhoneInput, isPhoneComplete} from '@/helpers/phone';
import {errorTimeMessage} from '@/helpers/errorHelper';

export default {
    name: 'FindUser',

    components: {
        YandexCaptcha,
        IconLogo
    },

    props: {
        isSmsAvailable: {
            type   : Boolean,
            default: false
        },
        isCallAvailable: {
            type   : Boolean,
            default: false
        }
    },

    validations: {
        phone: {
            required,
            phoneLength: isPhoneComplete
        },
        code: {
            required,
            minLength: minLength(6),
            maxLength: maxLength(6)
        },
        phoneCode: {
            required,
            minLength: minLength(4),
            maxLength: maxLength(4)
        }
    },

    data() {
        return {
            step              : 1, // Текущий шаг, 1 - ввод телефона, 2 - ввод кода из смс
            phone             : '', // Телефон
            code              : '', // Код из смс
            lastCodeLength    : 0, // Длина кода после последней синхронизации поля
            phoneCode         : '', // Последние 4 цифры номера телефона
            codeType          : 'call', // Тип кода, sms - СМС, call - Звонок
            isCodeError       : false, // Есть ли ошибка с кодом
            codeErrorText     : 'Неверный код, попробуйте еще раз', // Текст при ошибке кода
            loaderMessage     : 'Проверка кода', // Текст при запуске лоадера
            isSMSSending      : false, // отправляется ли СМС
            isCallRequesting  : false, // отправляется ли запрос на звонок
            loading           : false, // Запущен ли лоадер
            hasLoaded         : false, // Завершилась ли загрузка успехом
            isCanSendCode     : false, // Можно ли отправить код
            timeShowResult    : 1500, // Время показа успеха при удачном запросе
            loginAgreement    : false, // чекбокс солгасия
            captchaExecuter   : null, // метод вызова капчи
            otpAbortController: null, // отмена Web OTP API
            resultTimer       : null, // таймер для скрытия результата,
            resendAfterExpiry : false,
            captchaResolved   : false
        };
    },

    computed: {
        ...mapGetters({
            agreementText: 'GET_AGREEMENT'
        }),

        isPhoneInvalid() {
            return this.$v.phone.$invalid;
        },

        isSMSInvalid() {
            return this.$v.code.$invalid;
        },

        isPhoneCodeInvalid() {
            return this.$v.phoneCode.$invalid;
        },

        clearPhone() {
            return this.phone.replace(/\D+/g, '').replace(/^\d/, '7');
        },

        /**
         * Формирует уникальный идентификатор для иконки лоадера
         * @return {string}
         */
        getRandomId() {
            return '-icon-load-' + Utils.generateRandomValue() + '-' + Utils.generateRandomValue();
        }
    },

    watch: {
        step(step) {
            if (step === 2) {
                this.$nextTick(() => {
                    this.$refs.codeInput?.focus();
                });
            } else {
                this.stopOtpListener();
            }
        }
    },

    created() {
        if (!this.agreementText) {
            this.getAgreement();
        }
    },

    beforeDestroy() {
        this.resendAfterExpiry = false;
        this.stopOtpListener();
        clearTimeout(this.resultTimer);
    },

    methods: {
        ...mapActions(['sendSMS', 'sendCall', 'checkSMSCode', 'findCounterpart', 'getAgreementText']),

        getCaptchaExecuter(captchaExecuter) {
            this.captchaExecuter = captchaExecuter || null;
        },

        async captchaCallback(token) {
            if (!token) {
                this.onCaptchaError();
                return;
            }

            this.captchaResolved = true;

            if (this.codeType === 'sms') {
                await this.sendSMSToCRM(token);
                return;
            }
            await this.sendCallToCRM(token);
        },


        async checkCaptcha(type = 'sms') {
            this.captchaResolved = false;

            if (this.step === 2) {
                this.code = '';
                this.phoneCode = '';

                this.clearCodeError();

                if (this.codeType !== 'call') {
                    this.$refs.codeInput?.focus();
                }
            }
            
            if (type === 'call') {
                this.isCallRequesting = true;
                this.codeType = 'call';
            } else {
                this.isSMSSending = true;
                this.codeType = 'sms';
                this.startOtpListener();
            }

            if (process.env.NODE_ENV === 'development') {
                await (type === 'sms' ? this.sendSMSToCRM() : this.sendCallToCRM());
                return;
            }

            if (!this.captchaExecuter) {
                this.isSMSSending = false;
                this.isCallRequesting = false;
                this.returnError({code: 1});
                return;
            }

            this.captchaExecuter();
        },

        async sendCallToCRM(token = null) {
            try {
                this.isCallRequesting = true;
    
                const response = await this.sendCall({phone: this.clearPhone, captchaToken: token});

                this.isCallRequesting = false;
    
                const {data} = response;
    
                if (data?.error) {
                    this.resendAfterExpiry = false;

                    this.returnError({
                        code: data?.notCounter ? 1 : 3
                    });

                    return;
                }
    
                // messageCode
                // 0 - Ошибка интеграции
                // 1 - Звонок заказан
                // 2 - Звонок не заказан
                // 3 - Номер в черном списке
                // 4 - Превышено количество попыток ввода
                const messageCode = data?.data?.messageCode;

                switch(messageCode) {
                    case 1:
                        // Временно блокируем повторную отправку смс
                        this.isCanSendCode = false;
                        // Переходим к вводу пароля
                        this.step = 2;
                        if (this.resendAfterExpiry) {
                            this.resendAfterExpiry = false;
                            this.showCodeError(3);
                        }
                        break;
                    case 3:
                        this.resendAfterExpiry = false;
                        this.returnError({
                            code: 2,
                            data: {
                                message: `Ваш аккаунт заблокирован. Позвоните нам для разблокировки`,
                            }
                        });
                        return;
                    case 4:
                        this.resendAfterExpiry = false;
                        const time = data.data.blockTerm || 20;
                        const minutesBlock = time / 60;

                        this.returnError({
                            code: 2,
                            data: {
                                message: errorTimeMessage(time),
                                time   : minutesBlock
                            }
                        });
                        return;
                    default:
                        this.resendAfterExpiry = false;
                        this.returnError({
                            code: 1
                        });
                        return;
                }
            } catch (error) {
                this.resendAfterExpiry = false;
                this.isCallRequesting = false;
                this.launchLoader(false);
                this.returnError({
                    code: 5,
                    data: error.response
                });
            } finally {
                this.$refs.yandexCaptcha?.reset();
            }
        },


        /**
         * Отправляет SMS код пользователю
         */
        async sendSMSToCRM(token = null) {
            try {
                this.isSMSSending = true;

                // Показываем ли тут лоадер или блочим
                const response = await this.sendSMS({phone: this.clearPhone, captchaToken: token});
                
                this.isSMSSending = false;
                
                const {data} = response;
    
                if (data?.error) {
                    this.resendAfterExpiry = false;
                    this.returnError({
                        code: data?.notCounter ? 1 : 3
                    });

                    return;
                }
    
                // messageCode
                // 0 - Ошибка интеграции
                // 1 - SMS отправлено
                // 2 - SMS не отправлено
                // 3 - Номер в черном списке
                // 4 - Превышено количество попыток ввода
                const messageCode = data?.data?.messageCode;

                switch(messageCode) {
                    case 1:
                        // Временно блокируем повторную отправку смс
                        this.isCanSendCode = false;
                        // Переходим к вводу пароля
                        this.step = 2;

                        if (this.resendAfterExpiry) {
                            this.resendAfterExpiry = false;
                            this.showCodeError(3);
                        }
                        break;
                    case 3:
                        this.resendAfterExpiry = false;
                        this.returnError({
                            code: 2,
                            data: {
                                message: `Ваш аккаунт заблокирован. Позвоните нам для разблокировки`,
                            }
                        });
                        return;
                    case 4:
                        this.resendAfterExpiry = false;
                        const time = data.data.blockTerm || 20;
                        const minutesBlock = time / 60;

                        this.returnError({
                            code: 2,
                            data: {
                                message: errorTimeMessage(time),
                                time   : minutesBlock
                            }
                        });
                        return;
                    default:
                        this.resendAfterExpiry = false;
                        this.returnError({
                            code: 1
                        });
                        return;
                }
            } catch (error) {
                this.isSMSSending = false;
                this.resendAfterExpiry = false;
                this.stopOtpListener();
                this.launchLoader(false);
                this.returnError({
                    code: 5,
                    data: error.response
                });
            } finally {
                this.$refs.yandexCaptcha?.reset();
            }
        },

        /**
         * Проверяет введённый SMS код / последние 4 цифры номера телефона и перекидывает на нужный шаг
         */
        async checkSMSCodeInCRM() {
            try {
                if (this.loading) {
                    return;
                }
    
                this.stopOtpListener();
                this.launchLoader(true, 'Проверка кода');
    
                const response = await this.checkSMSCode({
                    phone   : this.clearPhone,
                    smsCode : this.codeType === 'sms' ? this.code : this.phoneCode,
                    codeType: this.codeType
                });

                const {data} = response;
    
                if (data?.error) {
                    this.returnError({
                        code: data?.notCounter ? 1 : 3
                    });
                    
                    return;
                }
    
                // messageCode
                // 0 - Ошибка интеграции
                // 1 - Код совпадает
                // 2 - Код не совпадает
                // 3 - Истекло время действия кода SMS
                // 4 - Превышено количество попыток ввода
                // 5 - Номер в черном списке
                const isShowError = !data?.data || !data?.data?.messageCode || data?.data?.messageCode === 0;

                if (isShowError) {
                    this.returnError({
                        code: 1
                    });
                    
                    return;
                }

                switch (data.data.messageCode) {
                    case 2:
                        this.launchLoader(false);
                        this.showCodeError(data.data.messageCode);
                        break;
                    case 3:
                        this.launchLoader(false);
                        this.resendAfterExpiry = true;
                        await this.checkCaptcha(this.codeType);
                        break;
                    case 4:
                        const time = data.data.blockTerm || 20;
                        const minutesBlock = time / 60;
                        const message = errorTimeMessage(time);

                        this.returnError({
                            code: 2,
                            data: {
                                message,
                                time: minutesBlock
                            }
                        });
                        this.launchLoader(false);
                        break;
                    case 5:
                        this.returnError({
                            code: 2,
                            data: {
                                message: `Ваш аккаунт заблокирован. Позвоните нам для разблокировки`,
                            }
                        });
                        this.launchLoader(false);
                        break;
                    default:
                        this.findUser();
                        break;
                }
            } catch (error) {
                this.launchLoader(false);
                this.returnError({
                    code: 5,
                    data: error.response
                });
            }
        },

        /**
         * Ищет пользователя
         */
        async findUser() {
            try {
                const response = await this.findCounterpart({
                    phone: this.clearPhone
                });

                const {data} = response;
    
                if (data?.error) {
                    this.returnError({
                        code: data?.notCounter ? 1 : 3
                    });

                    return;
                }
    
                // messageCode
                // 0 - Ошибка интеграции
                // 1 - Контрагент найден в СРМ, ранее авторизовался
                // 2 - Контрагент найден в СРМ, ранее НЕ авторизовался
                // 3 - Контрагент не найден, введите регистрационные данные
                // 4 - Номер телефона обязателен для заполнения (в теории не
                // может быть заполнен номер телефона)
                const messageCode = data?.data?.messageCode;
                const isShowError = !messageCode || messageCode === 0 || messageCode === 4;

                if (isShowError) {
                    this.returnError({
                        code: 1
                    });
                    
                    return;
                }

                switch (messageCode) {
                    case 1:
                        // Сохраняем userId в store
                        this.$store.dispatch('addUserId', {
                            userId: data.data.userId
                        });

                        // Surname (проверка фамилии)
                        this.goToNext(3);
                        // Показываем успех запроса
                        this.completeLoader();
                        break;
                    case 2:
                        // Сохраняем userId в store
                        this.$store.dispatch('addUserId', {
                            userId: data.data.userId
                        });

                        // Переходим к регистрации
                        this.goToNext(2);
                        // Показываем успех запроса
                        this.completeLoader();
                        break;
                    case 3:
                        // Переходим к регистрации
                        this.goToNext(2);
                        // Показываем успех запроса
                        this.completeLoader();
                        break;
                    default:
                        break;
                }
            } catch (error) {
                this.launchLoader(false);
                this.returnError({
                    code: 5,
                    data: error.response
                });
            }
        },

        /**
         * Получает тектс политики
         */
        async getAgreement() {
            try {
                await this.getAgreementText();
            } catch (error) {
                if (error && error.code === AUTH_CHECK_FAILED) {
                    return;
                }

                throw error;
            }
        },

        /**
         * Показывает ошибку при неправильном коде
         * @param code {String} - код ошибки, 2 - Код не совпадает, 3 - Истекло время действия кода SMS
         */
        showCodeError(code) {
            this.isCodeError = true;

            switch (code) {
                case 2:
                    this.codeErrorText = 'Неверный код, попробуйте еще раз';
                    break;
                case 3:
                    this.codeErrorText = 'Срок действия кода истёк. Мы отправили запрос на получение нового кода. Введите его.';
                    break;
                default:
                    this.codeErrorText = 'Непредвиденная ошибка, попробуйте позже';
                    break;
            }
        },

        /**
         * Сбрасывает ошибку при вводе кода
         */
        clearCodeError() {
            this.isCodeError = false;
        },

        onCodeInput(event) {
            // v-model уже мог обновить code, поэтому берём длину после прошлой синхронизации
            const prevLength = this.lastCodeLength || 0;

            this.syncCodeFromInput(event);

            const filledAtOnce = this.code.length === 6 && prevLength < 6 && event.inputType !== 'insertText';

            if (filledAtOnce) {
                this.checkSMSCodeInCRM();
            }
        },

        syncCodeFromInput(event) {
            const rawValue = event?.target?.value ?? this.$refs.codeInput?.value ?? '';
            const value = rawValue.replace(/\D/g, '').slice(0, 6);

            if (this.code !== value) {
                this.code = value;
            }

            this.lastCodeLength = value.length;
        },

        onCodeAutofillAnimation(event) {
            if (event.animationName === 'findUserCodeAutoFillStart') {
                this.syncCodeFromInput();

                this.$nextTick(() => {
                    if (!this.isSMSInvalid) {
                        this.checkSMSCodeInCRM();
                    }
                });
            }
        },

        onCodeEnter(event) {
            event.preventDefault();

            if (this.step === 1) {
                if (this.isPhoneInvalid || (this.agreementText && !this.loginAgreement)) {
                    return;
                }

                if (this.isSmsAvailable && !this.isCallAvailable && !this.isCallRequesting && !this.isSMSSending) {
                    this.checkCaptcha();
                }

                if (this.isCallAvailable && !this.isSmsAvailable && !this.isSMSSending && !this.isCallRequesting) {
                    this.checkCaptcha('call');
                }
            } else if (this.step === 2) {
                const invalid = this.codeType === 'sms' ? this.isSMSInvalid : this.isPhoneCodeInvalid;

                if (!invalid) {
                    this.checkSMSCodeInCRM();
                }
            }
        },

        applyOtpCode(otpCode) {
            const value = String(otpCode).replace(/\D/g, '').slice(0, 6);

            if (!value) {
                return;
            }

            this.code = value;
            this.lastCodeLength = value.length;

            this.$nextTick(() => {
                const input = this.$refs.codeInput;

                if (input && input.value !== value) {
                    input.value = value;
                }

                if (value.length === 6) {
                    this.checkSMSCodeInCRM();
                }
            });
        },

        startOtpListener() {
            if (!('OTPCredential' in window)) {
                return;
            }

            this.stopOtpListener();
            this.otpAbortController = new AbortController();

            navigator.credentials.get({
                otp   : { transport: ['sms'] },
                signal: this.otpAbortController.signal
            })
                .then((otp) => {
                    if (otp?.code) {
                        this.applyOtpCode(otp.code);
                    }
                })
                .catch(() => {});
        },

        stopOtpListener() {
            if (this.otpAbortController) {
                this.otpAbortController.abort();
                this.otpAbortController = null;
            }
        },

        onPhoneFocus() {
            this.$v.phone.$touch();
            handlePhoneFocus(this, 'phone', 'phoneInput');
        },

        onPhoneInput(value) {
            handlePhoneInput(this, 'phone', 'phoneInput', value);
            this.$v.phone.$touch();
        },

        onPhoneBlur() {
            if (this.phone.replace(/\D/g, '').length < 11) {
                this.phone = '';
            }
        },

        /**
         * Переход к следующему шагу
         * @param step {Number} - шаг к которому нужно перейти в общей странице авторизации
         */
        goToNext(step) {
            this.$emit('goToNext', step);
        },

        /**
         * Возвращает ошибку в общую форму
         * @param code {Number} - код ошибки. 1 - ошибка обработки запроса в CRM. 2 - Блокировка на время. 3 - Ошибка со счётчиком попыток. 4 - Блокировка при привышении кол-ва попыток. 5 - Ошибка на сервере
         * @param data {Object} - данные для показа ошибки
         */
        returnError({code, data = null}) {
            this.$emit('showError', {code, data});
        },

        /**
         * Запуск/остановка лоадера
         * @param status {Boolean} - Вкл/выкл лоадер
         * @param message {String} - текст лоадера
         */
        launchLoader(status, message = '') {
            this.loading = status;
            this.loaderMessage = message;
        },

        /**
         * Успешное завершение лоадера лоадера
         */
        completeLoader() {
            this.hasLoaded = true;
            this.launchLoader(false);

            this.resultTimer = setTimeout(() => {
                this.resultTimer = null;
                this.hasLoaded = false;
            }, this.timeShowResult);
        },

        onCaptchaError() {
            this.resendAfterExpiry = false;
            this.isSMSSending = false;
            this.isCallRequesting = false;
            this.stopOtpListener();
            this.returnError({code: 1});
        },

        onChallengeHidden() {
            if (this.captchaResolved) {
                return;
            }

            this.isSMSSending = false;
            this.isCallRequesting = false;
            this.stopOtpListener();
        }
    }
};
</script>

<style lang="scss" scoped>
@keyframes findUserCodeAutoFillStart {
    from {}

    to {}
}

.login__form-code-input:-webkit-autofill {
    animation-name: findUserCodeAutoFillStart;
}
</style>
